import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  Paper,
  Avatar,
  Rating,
  useTheme,
  useMediaQuery,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  ArrowForward,
  Star,
  Verified,
  LocalShipping,
  Security,
  Support,
  TrendingUp,
  ShoppingCart,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { fetchFeaturedProducts, fetchProductCategories } from '../store/slices/productSlice';
import { addToCart } from '../store/slices/cartSlice';
import { fetchHomePageStatistics } from '../store/slices/analyticsSlice';
import { bannerService } from '../services/bannerService';
import { settingsService } from '../services/settingsService';
import { reviewService } from '../services/reviewService';
import { toast } from 'react-toastify';
import { getProductPrimaryImageUrl } from '../utils/imageUtils';
import BannerCarousel from '../components/carousel/BannerCarousel';

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url("https://images.pexels.com/photos/7031589/pexels-photo-7031589.jpeg?_gl=1*1e71lnm*_ga*MTQ4MzEyODY1NS4xNzU3OTQ4ODQ0*_ga_8JE65Q40S6*czE3NTc5NDg4NDQkbzEkZzEkdDE3NTc5NDk2MjgkajU5JGwwJGgw")`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  color: theme.palette.primary.contrastText,
  minHeight: '70vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '280px', // Reduced height for square appearance
  width: '280px', // Made width equal to height for square shape
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[2],
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[10],
    borderColor: theme.palette.primary.main,
    '& .category-icon': {
      transform: 'scale(1.05)',
    },
  },
}));

const ProductCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
}));

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { featuredProducts, featuredLoading, categories, loading: categoriesLoading } = useSelector((state) => state.products);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { homePageStats, loading: analyticsLoading } = useSelector((state) => state.analytics);
  
  // Banner state
  const [banners, setBanners] = React.useState([]);
  const [bannerLoading, setBannerLoading] = React.useState(true);
  const [bannerSettings, setBannerSettings] = React.useState({ bannerEnabled: true });
  
  // State for category images (dynamically fetched)
  const [categoryImages, setCategoryImages] = React.useState({});
  
  // State for testimonials (real reviews)
  const [testimonials, setTestimonials] = React.useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = React.useState(true);
  const testimonialsInitializedRef = React.useRef(false);
  
  useEffect(() => {
    dispatch(fetchFeaturedProducts({ size: 8 }));
    dispatch(fetchHomePageStatistics());
    dispatch(fetchProductCategories());
    
    // Fetch active banners and settings for carousel
    const fetchBannersAndSettings = async () => {
      try {
        setBannerLoading(true);
        
        // Fetch banner settings
        const publicSettings = await settingsService.getPublicSettings();
        setBannerSettings(publicSettings);
        
        // Only fetch banners if they are enabled
        if (publicSettings.bannerEnabled) {
          const activeBanners = await bannerService.getActiveBanners();
          setBanners(Array.isArray(activeBanners) ? activeBanners : []);
        } else {
          setBanners([]);
        }
      } catch (error) {
        console.error('Error fetching banners and settings:', error);
        setBanners([]);
        setBannerSettings({ bannerEnabled: true }); // Default fallback
      } finally {
        setBannerLoading(false);
      }
    };
    
    fetchBannersAndSettings();
  }, [dispatch]);
  
  // Fetch category images dynamically for all categories - fetches most recently approved product
  // Memoize to prevent unnecessary re-fetches
  const categoryImagesFetchedRef = React.useRef(false);
  
  useEffect(() => {
    const fetchCategoryImages = async () => {
      // Wait for categories to be loaded
      if (!categories || categories.length === 0) {
        return;
      }
      
      // Prevent multiple simultaneous fetches
      if (categoryImagesFetchedRef.current) {
        return;
      }
      
      categoryImagesFetchedRef.current = true;
      
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';
        const imageMap = {};
        
        // Fetch images for all categories dynamically - batch requests to avoid excessive calls
        const fetchPromises = categories.slice(0, 10).map(async (category) => { // Limit to first 10 categories
          try {
            // Fetch most recently approved product (sorted by createdAt descending)
            const response = await fetch(
              `${API_BASE_URL}/products?category=${encodeURIComponent(category.name)}&page=0&size=1&status=ACTIVE&approvalStatus=APPROVED&sortBy=createdAt&sortDir=desc`
            );
            
            if (response.ok) {
              const data = await response.json();
              const products = data.content || [];
              
              // Get the most recent product with an image
              if (products.length > 0) {
                const product = products[0];
                // Prefer the Cloudinary thumbnail variant, fallback to the raw EC2 URL
                const imageUrl = getProductPrimaryImageUrl(product, false, 'thumbnail');

                if (imageUrl) {
                  imageMap[category.name] = imageUrl;
                }
              }
            }
          } catch (error) {
            console.error(`Error fetching image for category ${category.name}:`, error);
          }
        });
        
        // Wait for all category images to be fetched
        await Promise.all(fetchPromises);
        
        // Update state with all fetched images
        if (Object.keys(imageMap).length > 0) {
          setCategoryImages(imageMap);
        }
      } catch (error) {
        console.error('Error fetching category images:', error);
      } finally {
        // Reset flag after a delay to allow refresh
        setTimeout(() => {
          categoryImagesFetchedRef.current = false;
        }, 5000); // 5 second cooldown
      }
    };
    
    fetchCategoryImages();
    
    // Refresh category images every 5 minutes to show newly approved products
    const categoryImagesInterval = setInterval(() => {
      categoryImagesFetchedRef.current = false; // Reset flag before refresh
      fetchCategoryImages();
    }, 120000); // 2 minutes 
    
    return () => {
      clearInterval(categoryImagesInterval);
      categoryImagesFetchedRef.current = false;
    };
  }, [categories]);
  
  // Fetch recent 5-star reviews for testimonials - separate useEffect for auto-refresh
  useEffect(() => {
    const fetchTopReviews = async () => {
      try {
        setTestimonialsLoading(true);
        
        // Try to get top-rated reviews first (these are approved and sorted by rating)
        let reviews = [];
        try {
          const topResponse = await reviewService.getTopReviews({ 
            type: 'rated', 
            limit: 50 
          });
          reviews = topResponse?.reviews || [];
        } catch (topError) {
          console.warn('Error fetching top reviews, trying recent reviews:', topError);
          // Fallback to recent reviews with correct parameters
          const recentResponse = await reviewService.getRecentReviews({ 
            days: 30, 
            limit: 50 
          });
          reviews = recentResponse?.reviews || recentResponse?.content || recentResponse || [];
        }
        
        // Filter for approved 5-star reviews only (as per user requirement)
        const fiveStarReviews = reviews.filter(review => 
          review.rating === 5 && 
          (review.status === 'APPROVED' || !review.status)
        );
        
        // Sort 5-star reviews by date (most recent first)
        const sortedReviews = fiveStarReviews.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.publishedAt || 0);
          const dateB = new Date(b.createdAt || b.publishedAt || 0);
          return dateB - dateA;
        });
        
        // Take top 3 reviews (all should be 5-star)
        const selectedReviews = sortedReviews.slice(0, 3).map(review => ({
          id: review.id,
          name: review.customerName || 
                (review.customer?.firstName ? `${review.customer.firstName} ${review.customer.lastName || ''}`.trim() : '') ||
                'Customer',
          role: review.customer?.role || 'Customer',
          avatar: review.customer?.profileImageUrl || '/images/avatar1.jpg',
          rating: review.rating || 5,
          comment: review.comment || review.reviewText || review.title || 'Great product!',
          createdAt: review.createdAt || review.publishedAt
        }));
        
        // Always update testimonials with fetched reviews (no fallback)
        if (selectedReviews.length > 0) {
          setTestimonials(selectedReviews);
          testimonialsInitializedRef.current = true;
        } else {
          // If no reviews found, set empty array
          setTestimonials([]);
          testimonialsInitializedRef.current = true;
        }
      } catch (error) {
        console.error('Error fetching top reviews:', error);
        // Don't set fallback testimonials - show empty state instead
        if (!testimonialsInitializedRef.current) {
          setTestimonials([]);
          testimonialsInitializedRef.current = true;
        }
      } finally {
        setTestimonialsLoading(false);
      }
    };
    
    // Fetch immediately
    fetchTopReviews();
    
    // Refresh reviews every 2 minutes to show new 5-star reviews
    const reviewsInterval = setInterval(() => {
      fetchTopReviews();
    }, 120000); // 2 minutes
    
    return () => clearInterval(reviewsInterval);
  }, []);
  

  
  const features = [
    {
      icon: <Verified sx={{ fontSize: 40, color:'white' }} />,
      title: 'Quality Assured',
      description: 'Every product undergoes rigorous quality inspection and certification by our material experts before listing',
    },
    {
      icon: <LocalShipping sx={{ fontSize: 40 , color:'white'}} />,
      title: 'Fast Delivery',
      description: 'Nationwide delivery network with real-time tracking and secure packaging for building materials',
    },
    {
      icon: <Security sx={{ fontSize: 40 ,color:'white'}} />,
      title: 'Secure Payments',
      description: 'Bank-grade encryption with multiple payment options including UPI, cards, and cash on delivery',
    },
    {
      icon: <Support sx={{ fontSize: 40,color:'white' }} />,
      title: '24/7 Support',
      description: 'Dedicated customer support team available round-the-clock for technical assistance and queries',
    },
  ];
  
  // Generate stats from real data
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M+`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K+`;
    return `${num}+`;
  };

  const stats = [
    { 
      label: 'Happy Customers', 
      value: homePageStats?.totalCustomers ? formatNumber(homePageStats.totalCustomers) : '10,000+', 
      icon: '😊' 
    },
    { 
      label: 'Orders Completed', 
      value: homePageStats?.totalOrders ? formatNumber(homePageStats.totalOrders) : '50,000+',
      icon: '📦' 
    },
    { 
      label: 'Verified Sellers', 
      value: homePageStats?.totalSellers ? formatNumber(homePageStats.totalSellers) : '1,000+', 
      icon: '✅' 
    },
    { 
      label: 'Total Revenue', 
      value: '100+', // Static fallback since revenue data not available in public endpoint
      icon: '🏙️' 
    },
  ];
  
  
  // Category image mapping - all images are fetched dynamically from most recently approved products
  const categoryImageMap = categoryImages;

  const handleCategoryClick = (category) => {
    navigate(`/products?category=${encodeURIComponent(category.name)}`);
  };
  
  const handleProductClick = (product) => {
    navigate(`/products/${product.id}`);
  };
  
  const handleAddToCart = (product, event) => {
    event.stopPropagation();
    const quantity = 1;
    dispatch(addToCart({ product, quantity }));
    toast.success(`${product.name} added to cart!`);
    // navigate('/cart'); // Removed to maintain current page view
  };
  
  return (
    <Box>
      {/* Dynamic Banner Section - Only show if banners are enabled */}
      {bannerSettings.bannerEnabled && (
        <BannerCarousel 
          banners={banners} 
          loading={bannerLoading} 
          onNavigate={navigate}
          isAuthenticated={isAuthenticated}
          user={user}
        />
      )}
      
      {/* Categories Section */}
      <Box sx={{ 
        bgcolor: 'grey.50', 
        py: 8,
        // Add top margin when banners are disabled to move content up
        mt: !bannerSettings.bannerEnabled ? 0 : 0
      }}> {/* Increased breathing space */}
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}> {/* Increased breathing space between title and cards */}
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 2 }}> {/* Restored spacing */}
              Shop by Category
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Explore our wide range of premium building materials
            </Typography>
          </Box>
          
          <Grid container spacing={3} sx={{ justifyContent: 'center', alignItems: 'stretch' }}> {/* Restored spacing */}
            {categories.map((category, index) => (
              <Grid item xs={3} sm={3} md={3} lg={3} xl={3} key={category.id} sx={{ 
                display: 'flex',
                justifyContent: 'center',
                width: '270px', // Fixed width to match card width
                flex: '0 0 270px' // Ensure exactly 4 cards per row with fixed width
              }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <FeatureCard
                    onClick={() => handleCategoryClick(category)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box
                      className="category-icon"
                      sx={{
                        height: '140px', // Reduced height to fit square card
                        bgcolor: category.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.3s ease-in-out',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      {categoryImageMap[category.name] ? (
                        <img
                          src={categoryImageMap[category.name]}
                          alt={category.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease-in-out',
                          }}
                          onError={(e) => {
                            // Fallback to emoji if image fails to load
                            e.target.style.display = 'none';
                            if (e.target.parentElement) {
                              e.target.parentElement.innerHTML = '<div style="font-size: 4rem;">🏗️</div>';
                            }
                          }}
                        />
                      ) : (
                        <Box sx={{ fontSize: '4rem' }}>🏗️</Box>
                      )}
                    </Box>
                    <CardContent sx={{ 
                      flexGrow: 1, 
                      p: 2, // Fixed padding for uniformity
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      height: '140px' // Adjusted content height for square card
                    }}>
                      <Box>
                        <Typography 
                          variant="h6" 
                          component="h3" 
                          sx={{ 
                            fontWeight: 700, 
                            mb: 1, // Fixed margin for uniformity
                            color: 'text.primary',
                            fontSize: '1.2rem', // Increased font size for better readability
                            lineHeight: 1.2,
                            textAlign: 'center'
                          }}
                        >
                          {category.name}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 1.5, // Fixed margin for uniformity
                            fontSize: '0.95rem', // Increased font size for better readability
                            lineHeight: 1.3,
                            textAlign: 'center',
                            height: '2.6em', // Fixed height for uniformity
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {category.description}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Chip
                          label={`${category.count} Products`}
                          size="small"
                          color="primary"
                          variant="filled"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.8rem', // Increased font size for better readability
                            height: '28px', // Fixed height for uniformity
                            '& .MuiChip-label': {
                              px: 1 // Fixed padding for uniformity
                            }
                          }}
                        />
                      </Box>
                    </CardContent>
                  </FeatureCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
          
          {/* Explore All Categories Button */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/products')}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              Explore All Categories
            </Button>
          </Box>
        </Container>
      </Box>
      
      {/* Featured Products Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
            Featured Products
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Handpicked premium products from our trusted sellers
          </Typography>
        </Box>
        
        {featuredLoading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ height: 200, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ height: 20, bgcolor: 'grey.300', mb: 1, borderRadius: 1 }} />
                    <Box sx={{ height: 16, bgcolor: 'grey.200', mb: 2, borderRadius: 1 }} />
                    <Box sx={{ height: 20, bgcolor: 'grey.300', width: '60%', borderRadius: 1 }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ 
            overflowX: 'auto', 
            pb: 2,
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            '&::-webkit-scrollbar': { 
              height: '8px' 
            }, 
            '&::-webkit-scrollbar-track': { 
              background: '#f1f1f1', 
              borderRadius: '4px' 
            }, 
            '&::-webkit-scrollbar-thumb': { 
              background: '#888', 
              borderRadius: '4px',
              '&:hover': { 
                background: '#555' 
              } 
            } 
          }}>
            <Grid container spacing={2} sx={{ display: 'flex', flexWrap: 'nowrap', width: 'max-content', margin: '0 auto' }}>
              {featuredProducts && featuredProducts.length > 0 ? (
                featuredProducts.slice(0, 8).map((product, index) => (
                  <Grid item key={product.id} sx={{ minWidth: '280px', maxWidth: '280px', flexShrink: 0 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    whileHover={{ y: -8 }}
                  >
                    <Card
                      className="card-effect"
                      sx={{
                        height: '520px',
                        width: '280px',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 0,
                        overflow: 'hidden',
                        bgcolor: '#dee2e6',
                        cursor: 'pointer',
                        border: 'none',
                        p: 0,
                        '&:hover .card-img': {
                          borderTopLeftRadius: '5px !important',
                          borderTopRightRadius: '5px !important',
                        },
                        '&:hover .card-img img': {
                          transform: 'scale(1.2)',
                          borderTopLeftRadius: '5px',
                          borderTopRightRadius: '5px',
                        },
                        '&:hover .img-hover::before': {
                          left: 0,
                          right: 0,
                          opacity: 0,
                        },
                        '&:hover .img-hover::after': {
                          top: 0,
                          bottom: 0,
                          opacity: 0,
                        },
                      }}
                      onClick={() => handleProductClick(product)}
                    >
                      {/* Image Container with Hover Effects */}
                      <Box 
                        className="card-img"
                        sx={{ 
                          position: 'relative',
                          height: '280px',
                          width: '100%',
                          overflow: 'hidden',
                          bgcolor: 'grey.100',
                        }}
                      >
                        {/* Shimmer Effect Container */}
                        <Box
                          className="img-hover"
                          sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            width: '100%',
                            height: '100%',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              pointerEvents: 'none',
                              opacity: 1,
                              zIndex: 3,
                              top: '-10%',
                              right: '51%',
                              bottom: '-10%',
                              left: '50%',
                              background: 'rgba(255,255,255,0.3)',
                              transition: 'all 900ms linear',
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              pointerEvents: 'none',
                              opacity: 1,
                              zIndex: 3,
                              top: '50%',
                              right: '-10%',
                              bottom: '50%',
                              left: '-10%',
                              background: 'rgba(255,255,255,0.6)',
                              transition: 'all 900ms linear',
                            },
                          }}
                        >
                          <CardMedia
                            component="img"
                            height="280"
                            image={getProductPrimaryImageUrl(product, false, 'medium') || '/mos-logo.jpg'}
                            alt={product.name}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              transition: 'transform 0.6s ease',
                              filter: getProductPrimaryImageUrl(product)
                                ? 'none'
                                : 'grayscale(100%)',
                              opacity: getProductPrimaryImageUrl(product)
                                ? 1
                                : 0.7,
                            }}
                            onError={(e) => {
                              e.target.src = '/mos-logo.jpg';
                              e.target.style.filter = 'grayscale(100%)';
                              e.target.style.opacity = '0.7';
                            }}
                          />
                        </Box>
                        
                        {/* Featured Badge */}
                        <Chip
                          label="Featured"
                          color="secondary"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 2,
                          }}
                        />
                      </Box>
                      
                      {/* Content Section - Fixed height for consistency */}
                      <Box 
                        className="card-body"
                        sx={{ 
                          height: '240px',
                          display: 'flex',
                          flexDirection: 'column',
                          pt: 0,
                          px: 2,
                          pb: 2,
                          position: 'relative',
                          boxShadow: '0px 5px 24.25px 0.75px rgba(0,0,0,0.1)',
                          bgcolor: '#fff',
                          borderBottomRightRadius: '5px',
                          borderBottomLeftRadius: '5px',
                        }}
                      >
                        {/* Product Info */}
                        <Box sx={{ flex: 1, minHeight: 0, pt: 3, display: 'flex', flexDirection: 'column' }}>
                          {/* Product Name with Cart Icon Below */}
                          <Box sx={{ mb: 1.5 }}>
                            <Box sx={{ 
                              mb: 1.5,
                              textAlign: 'center',
                              display: 'grid',
                              gridTemplateColumns: '1fr auto 1fr',
                              alignItems: 'center',
                              gap: '10px',
                            }}>
                              <Box sx={{ borderTop: '6px double #ffe5f4' }} />
                              <Typography 
                                variant="h6" 
                                component="h3" 
                                sx={{ 
                                  fontWeight: 'bold',
                                  fontSize: '16px',
                                  textTransform: 'uppercase',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  lineHeight: 1.3,
                                  color: '#561778',
                                }}
                              >
                                {product.name}
                              </Typography>
                              <Box sx={{ borderTop: '6px double #ffe5f4' }} />
                            </Box>
                            
                            {/* Cart Icon - Leftmost side below name */}
                            {user && (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5 }}>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddToCart(product, e);
                                  }}
                                  sx={{
                                    bgcolor: '#2C3E50',
                                    color: '#ffffff',
                                    width: 40,
                                    height: 40,
                                    transition: 'all 0.5s ease',
                                    backgroundImage: 'linear-gradient(to right top, #2C3E50, #2C3E50)',
                                    '&:hover': {
                                      backgroundImage: 'linear-gradient(to right top, #2C3E50, #2C3E50)',
                                      transform: 'scale(1.05)',
                                    },
                                  }}
                                >
                                  <ShoppingCart sx={{ fontSize: '20px' }} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                          
                          {/* Category and Origin */}
                          <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <Chip 
                              label={product.category} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '10px', height: '20px' }}
                            />
                            {product.origin && (
                              <Chip 
                                label={product.origin} 
                                size="small" 
                                variant="outlined"
                                color="secondary"
                                sx={{ fontSize: '10px', height: '20px' }}
                              />
                            )}
                          </Box>

                          {/* Product Details */}
                          <Box sx={{ mb: 1 }}>
                            {/* Color and Finish */}
                            {(product.specifications?.color || product.specifications?.finish) && (
                              <Box sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                                {product.specifications?.color && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box 
                                      sx={{ 
                                        width: 12, 
                                        height: 12, 
                                        borderRadius: '50%', 
                                        bgcolor: product.specifications.color.toLowerCase(),
                                        border: '1px solid #ddd'
                                      }} 
                                    />
                                    <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                      {product.specifications.color}
                                    </Typography>
                                  </Box>
                                )}
                                {product.specifications?.finish && (
                                  <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                    {product.specifications.finish}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            
                            {/* Description */}
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                fontSize: '11px',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mb: 1,
                                textAlign: 'center',
                              }}
                            >
                              {product.description || 'Premium quality building material'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Price Section */}
                        <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 700, fontSize: '18px' }}>
                              ₹{(product.discountedPrice || product.price)?.toFixed(2)}
                            </Typography>
                            {product.discountedPrice && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  textDecoration: 'line-through', 
                                  color: 'text.secondary',
                                  fontSize: '14px'
                                }}
                              >
                                ₹{product.price?.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px', mb: 0.5 }}>
                            +GST                           
                          </Typography>
                          
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
                              {product.priceUnit || 'sq ft'}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary">
                        No featured products available at the moment
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            size="large"
            endIcon={<ArrowForward />}
            onClick={() => navigate('/products')}
          >
            View All Products
          </Button>
        </Box>
      </Container>
      
      {/* Features Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Why Choose MOS Marketplace?
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              We're committed to providing the best experience for our customers
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'flex-start',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: { xs: 2, sm: 3, md: 4 },
            maxWidth: '1000px',
            mx: 'auto'
          }}>
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <Box sx={{ 
                  textAlign: 'center',
                  minWidth: { xs: '120px', sm: '140px', md: '200px' },
                  maxWidth: { xs: '140px', sm: '160px', md: '220px' },
                  flex: { md: '1' }
                }}>
                  <Box sx={{ 
                    color: '#a4adb5', 
                    mb: 1,
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(feature.icon, { sx: { fontSize: { xs: 32, md: 36 } } })}
                  </Box>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    mb: 0.5, 
                    fontSize: { xs: '0.85rem', md: '0.9rem' }
                  }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    opacity: 0.85, 
                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                    lineHeight: 1.2,
                    display: { xs: 'none', sm: 'block' }
                  }}>
                    {feature.description}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>
      
      {/* Testimonials Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
            What Our Customers Say
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Real feedback from our satisfied customers
          </Typography>
        </Box>
        
        {testimonialsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : testimonials.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id || index}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.6, 
                  delay: index * 0.15,
                  ease: [0.25, 0.46, 0.45, 0.94] // Custom easing for smooth animation
                }}
                style={{ width: '100%', maxWidth: '700px' }}
              >
                <Card 
                  sx={{ 
                    width: '100%',
                    maxWidth: '700px',
                    p: 4,
                    borderRadius: 4,
                    boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
                    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '4px',
                      height: '100%',
                      bgcolor: 'primary.main',
                      transform: 'scaleY(0)',
                      transformOrigin: 'bottom',
                      transition: 'transform 0.4s ease-in-out',
                    },
                    '&:hover': {
                      transform: 'translateX(8px)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      borderColor: 'primary.main',
                      '&::before': {
                        transform: 'scaleY(1)',
                      }
                    }
                  }}
                >
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                      {/* Avatar */}
                      <Box sx={{ flexShrink: 0 }}>
                        <Avatar 
                          sx={{ 
                            width: 72, 
                            height: 72, 
                            bgcolor: 'primary.main',
                            fontSize: '1.75rem',
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05) rotate(5deg)',
                            }
                          }}
                        >
                          {testimonial.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </Box>
                      
                      {/* Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Name and Role */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 600,
                                color: 'text.primary',
                                mb: 0.5
                              }}
                            >
                              {testimonial.name}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {testimonial.role}
                            </Typography>
                          </Box>
                          
                          {/* Rating */}
                          <Rating 
                            value={testimonial.rating} 
                            readOnly 
                            size="small"
                            sx={{
                              '& .MuiRating-iconFilled': {
                                color: '#fbbf24',
                              },
                            }}
                          />
                        </Box>
                        
                        {/* Comment */}
                        <Box sx={{ position: 'relative', pl: 3 }}>
                          <Typography 
                            component="span"
                            sx={{ 
                              fontSize: '4rem',
                              color: 'primary.light',
                              opacity: 0.2,
                              fontFamily: 'Georgia, serif',
                              position: 'absolute',
                              left: 0,
                              top: -20,
                              lineHeight: 1,
                            }}
                          >
                            "
                          </Typography>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontStyle: 'italic',
                              color: 'text.secondary',
                              lineHeight: 1.8,
                              fontSize: '1rem',
                              position: 'relative',
                              zIndex: 1,
                            }}
                          >
                            {testimonial.comment}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No reviews available at the moment
            </Typography>
          </Box>
        )}
      </Container>
      
      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3} justifyContent="center">
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                style={{ width: '100%', maxWidth: '250px' }}
              >
                <StatsCard elevation={2}>
                  <Typography variant="h2" sx={{ fontSize: '2rem', mb: 1 }}>
                    {stat.icon}
                  </Typography>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </StatsCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* CTA Section */}
      <Box sx={{ bgcolor: '#f7e7ce', color: 'secondary.contrastText', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Ready to Start Your Project?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Join thousands of satisfied customers and find the perfect materials for your project
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                //color="primary"
                size="large"
                onClick={() => navigate('/products')}
                sx={{ 
                  px: 4,
                  py: 1.5 ,
                  '&:hover': {
                    variant:"outlined",
                    color:"white",
                    borderWidth: 2,
                    backgroundColor: '#2C3E50',
                    borderColor: '#2C3E50',
                    size:"large",
                  },
                }}
              >
                Browse Products
              </Button>
              <Button
                variant="outlined"
                color="#2C3E50"
                size="large"
            
                onClick={() => navigate('/contact')}
                sx={{
                  px: 4,
                  py: 1.5,
                  color: '#2C3E50',
                  borderWidth: 2,
                  borderColor: '#2C3E50',
                  '&:hover': {
                    borderColor: '#2C3E50',
                    color: 'white',
                    backgroundColor: '#2C3E50',
                  },
                }}
              >
                Contact Us
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;